import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, ComponentRef, effect, ElementRef, Injector, OnDestroy, OnInit, signal, ViewContainerRef, viewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Cluster, MarkerClusterer } from '@googlemaps/markerclusterer';
import { QueryConstraint, where } from 'firebase/firestore';
import moment from 'moment';
import { fromEventPattern, map, Observable, pairwise, Subscription } from 'rxjs';
import { UserRole } from '../../../../core/auth/enums/userRoles';
import { Common } from '../../../../shared/enums/common';
import { DriverDocument } from '../../../../shared/interfaces/driver-firestore';
import { EnvConfigService } from '../../../../shared/services/env-config.service';
import { FireStoreService } from '../../../../shared/services/firestore.service';
import { SingleMapService } from '../../../../shared/services/single-map.service';
import { StateService } from '../../../../shared/services/state.service';
import { RideListItemComponent } from '../../components/ride-list-item/ride-list-item.component';
import { DriverMapRideDto } from '../../interfaces/driver-map-ride';
import { DriverTooltipComponent } from './driver-tooltip/driver-tooltip.component';

const MODULES = [CommonModule];
const COMPONENTS = [RideListItemComponent];

type MarkerSeverity = 'success' | 'light-warning' | 'warning' | 'danger' | '';

@Component({
  selector: 'app-rides-list',
  standalone: true,
  imports: [...MODULES, ...COMPONENTS],
  templateUrl: './rides-list.component.html',
  styleUrl: './rides-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RidesListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly stateService = inject(StateService);
  private readonly firestoreService = inject(FireStoreService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly singleMapService = inject(SingleMapService);
  private readonly env = inject(EnvConfigService);

  selectedView = signal(1);
  findDriverDialogStatusSub!: Subscription;
  mapDialogStatusSub!: Subscription;
  ownerActivationSub!: Subscription;
  mapCollectionSubscription!: Subscription;
  partnersSubscription!: Subscription;
  zoomChangesSubscription!: Subscription;
  rideDriverMarkers = new Map<number, google.maps.marker.AdvancedMarkerElement>();
  partnersLocationsMap = new Map<number, DriverDocument>();
  private infoWindow!: google.maps.InfoWindow;
  private clusterer!: MarkerClusterer | null;

  private readonly driverRidesCache = new Map<number, DriverMapRideDto[]>(); // All rides per driver
  private readonly displayedDriverRides = new Map<
    number,
    {
      rideDriverData: DriverMapRideDto;
      marker: google.maps.marker.AdvancedMarkerElement;
    }
  >(); // Currently displayed ride per driver

  allRideIds: number[] = [];

  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();

  tooltipRef: ComponentRef<DriverTooltipComponent> | null = null;
  private openTooltipRideId: number | null = null;

  constructor() {
    // Effect to Attach/Detach map based on view signal and service readiness
    effect(
      () => {
        const view = this.selectedView();
        const serviceReady = this.singleMapService.isMapInitialized();
        if (view == 2 && serviceReady && !this.isMapAttachedHere) {
          // --- Attach Map ---
          // v19+ runs component effects before the view updates, so the map column may still be
          // hidden (0x0) here; attaching then leaves Google Maps blank until the next resize.
          if (!this.attachScheduled) {
            this.attachScheduled = true;
            afterNextRender(
              () => {
                this.attachScheduled = false;
                if (this.selectedView() == 2 && !this.isMapAttachedHere) {
                  this.attachAndSetupMap();
                }
              },
              { injector: this.injector },
            );
          }
        } else if (view != 2 && this.isMapAttachedHere) {
          // --- Detach Map ---
          this.cleanupMapResources(); // Clean THIS component's stuff FIRST
          this.singleMapService.releaseMap(this.mapOwnerId); // Release ownership back to previous owner/host
          this.mapInstance = null; // Clear local ref
          this.isMapAttachedHere = false;
          this.clearListeners();
        }
      },
      { injector: this.injector },
    );
  }

  readonly mapContainerRef = viewChild.required<ElementRef<HTMLDivElement>>('mapDiv');
  private mapInstance: google.maps.Map | null = null; // Local ref to the map
  private isMapAttachedHere = false; // Track attachment state for this component
  private attachScheduled = false;
  private componentMapListeners: google.maps.MapsEventListener[] = [];

  // Removed dialog open UI flags – map ownership now managed in service
  private readonly mapOwnerId = `rides-list`;

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe(params => {
      const defaultView = this.currentRole == UserRole.Client ? 2 : 1;
      this.selectedView.update(val => (params['view'] ? Number(params['view']) : defaultView));
    });

    // React to ownership changes to cleanup and restore listeners
    this.ownerActivationSub = this.singleMapService.currentOwnerId$.subscribe(ownerId => {
      if (ownerId === this.mapOwnerId) {
        // Map returned to this component: re-acquire local state and listeners
        this.mapInstance = this.singleMapService.getMapInstance();
        if (this.mapInstance && !this.isMapAttachedHere) {
          this.isMapAttachedHere = true;
          this.addMapListeners();
        }
      } else {
        // Map moved away: cleanup component-specific resources
        this.cleanupMapResources();
        this.isMapAttachedHere = false;
      }
    });
  }

  attachAndSetupMap() {
    const mapContainerRef = this.mapContainerRef();
    if (!mapContainerRef?.nativeElement) {
      return;
    }

    this.allRideIds = this.stateService.getFilteredIds();
    const componentMapOptions: google.maps.MapOptions = {
      ...this.singleMapService.initialMapOptions,
      gestureHandling: 'greedy',
    };
    // Get map from service and attach it here
    this.mapInstance = this.singleMapService.acquireMap(this.mapOwnerId, mapContainerRef.nativeElement, componentMapOptions);

    if (this.mapInstance) {
      this.isMapAttachedHere = true;
      // Add component-specific listeners/markers AFTER attaching
      this.addMapListeners();
    } else {
      console.error('[RidesList] Failed to attach map from service.');
    }
  }

  addMapListeners() {
    if (!this.mapInstance) return;
    this.zoomChangesSubscription = this.observeZoomChanges().subscribe(([previousZoom, currentZoom]) => {
      if (currentZoom < previousZoom) {
        this.infoWindow?.close();
      }
    });

    this.listenOnPartnerLocations();
    this.listenOnMapDetails();
  }

  onRidesChanged(rideIds: number[]) {
    this.allRideIds = rideIds;

    if (this.selectedView() == 2) {
      this.rideDriverMarkers.forEach(m => {
        m.map = null;
        google.maps.event.clearInstanceListeners(m);
        this.clusterer?.removeMarker(m);
      });
      this.rideDriverMarkers.clear();
      this.displayedDriverRides.clear();

      this.listenOnMapDetails();
    }
  }

  listenOnPartnerLocations() {
    if (this.partnersSubscription) {
      this.partnersSubscription?.unsubscribe();
    }
    this.partnersSubscription = this.firestoreService.listenToCollectionChanges(this.env.getConfig().partnerCollection).subscribe({
      next: res => {
        let { changes } = res;
        console.log(changes);
        changes.forEach((change: any) => {
          if (change.type === 'added' || change.type === 'modified') {
            this.partnersLocationsMap.set(change.data.partner_id, change.data);

            // If ride is displayed on map and NOT in progress => update location if live location is available
            // else => keep the last known location
            const displayedDriverRide = this.displayedDriverRides.get(change.data.partner_id)?.rideDriverData;
            if (displayedDriverRide && !displayedDriverRide.in_progress) {
              if (change.data.latest_active_location._lat && change.data.latest_active_location._long) {
                displayedDriverRide.latitude = change.data.latest_active_location._lat;
                displayedDriverRide.longitude = change.data.latest_active_location._long;
                const existingMarker = this.displayedDriverRides.get(change.data.partner_id)?.marker;
                if (existingMarker) {
                  this.updateDriverMarker(displayedDriverRide, existingMarker);
                }
              }
            }
          } else if (change.type === 'removed') {
            this.partnersLocationsMap.delete(change.data.partner_id);
          }
        });
      },
      error: err => console.log(err),
    });
  }

  listenOnMapDetails() {
    if (!this.isMapAttachedHere || !this.mapInstance) return;

    if (this.mapCollectionSubscription) {
      this.mapCollectionSubscription?.unsubscribe();
    }

    const constraints: QueryConstraint[] = [
      where('index_date_of_service', '==', moment().format('L')), // MM/DD/YYYY
      where('driver_id', '!=', Common.PartnerBotId),
    ];

    this.mapCollectionSubscription = this.firestoreService.listenToCollectionChanges(this.env.getConfig().mapDetailsCollection, constraints).subscribe({
      next: res => {
        let { changes } = res;
        console.log(changes);

        this.processMapDataChanges(changes as any[]);
      },
      error: err => console.log(err),
    });
  }

  processMapDataChanges(changes: { type: string; id: string; data: DriverMapRideDto }[]) {
    changes.forEach(change => {
      const partnerLiveLocation = this.partnersLocationsMap.get(change.data.driver_id);
      if (!change.data.in_progress && partnerLiveLocation) {
        change.data.latitude = partnerLiveLocation.latest_active_location._lat;
        change.data.longitude = partnerLiveLocation.latest_active_location._long;
      }
      const { type, data } = change;

      // Skip deleted rides
      if (data.ride_status.toLowerCase() === 'deleted') {
        this.removeRideFromCache(data);
        return;
      }

      // Update cache based on change type
      this.updateRideInCache(data, type);
    });

    // After updating cache, determine which rides should be displayed
    const ridesToDisplay = this.determineRidesToDisplay();

    if (this.rideDriverMarkers.size === 0) {
      this.addCarMarkers(ridesToDisplay);
    } else {
      this.updateMarkersWithNewRideUpdates(ridesToDisplay);
    }
  }

  private updateRideInCache(rideData: DriverMapRideDto, changeType: string) {
    const driverId = rideData.driver_id;
    const rideId = rideData.ride_id;

    // First, remove this ride from ALL driver caches (in case driver changed)
    this.removeRideFromAllDriverCaches(rideId);

    if (changeType == 'removed') {
      return;
    }

    if (!this.driverRidesCache.has(driverId)) {
      this.driverRidesCache.set(driverId, []);
    }

    const driverRides = this.driverRidesCache.get(driverId)!;
    const existingRideIndex = driverRides.findIndex(ride => ride.ride_id === rideData.ride_id);

    // Add or update the ride
    if (existingRideIndex !== -1) {
      driverRides[existingRideIndex] = rideData;
    } else {
      driverRides.push(rideData);
    }
  }

  private removeRideFromAllDriverCaches(rideId: number) {
    this.driverRidesCache.forEach((rides, driverId) => {
      const rideIndex = rides.findIndex(ride => ride.ride_id === rideId);
      if (rideIndex !== -1) {
        rides.splice(rideIndex, 1);

        // Clean up empty arrays
        if (rides.length === 0) {
          this.driverRidesCache.delete(driverId);
        }
      }
    });
  }

  private removeRideFromCache(rideData: DriverMapRideDto) {
    // Remove the ride from all driver caches
    this.removeRideFromAllDriverCaches(rideData.ride_id);
  }

  private determineRidesToDisplay(): DriverMapRideDto[] {
    const ridesToDisplay: DriverMapRideDto[] = [];
    this.driverRidesCache.forEach((rides, driverId) => {
      const selectedRide = this.selectBestRideForDriver(rides);
      if (selectedRide) {
        ridesToDisplay.push(selectedRide);
      }
    });

    return ridesToDisplay;
  }

  private selectBestRideForDriver(rides: DriverMapRideDto[]): DriverMapRideDto | null {
    if (rides.length === 0) return null;

    rides = rides.filter(d => this.allRideIds.includes(d.ride_id));

    // If only one ride, return it
    if (rides.length === 1) {
      return rides[0];
    }

    // Priority order (lower is higher priority):
    // 1) in progress  2) accepted  3) offered  4) open  5) closed
    const getPriority = (ride: DriverMapRideDto): number => {
      if (ride.in_progress) return 1;
      if (ride.is_accepted) return 2;
      if (ride.is_offered) return 3;
      if (!ride.is_closed) return 4; // open (scheduled) ride
      return 5; // closed
    };

    const compareRides = (a: DriverMapRideDto, b: DriverMapRideDto): number => {
      const priority_a = getPriority(a);
      const priority_b = getPriority(b);
      if (priority_a !== priority_b) return priority_a - priority_b;
      const time_a = a.scheduled_first_arrival_time ?? Number.MAX_SAFE_INTEGER;
      const time_b = b.scheduled_first_arrival_time ?? Number.MAX_SAFE_INTEGER;
      return time_a - time_b; // earliest first when same priority
    };

    // Select the best ride based on priority, then earliest time
    return rides.reduce((best, current) => (compareRides(current, best) < 0 ? current : best), rides[0]);
  }

  private updateMarkersWithNewRideUpdates(updatedRides: DriverMapRideDto[]) {
    // First, collect all drivers that currently have displayed rides
    const currentlyDisplayedDrivers = new Set(this.displayedDriverRides.keys());

    // Track which drivers should still have markers after this update
    const driversWithRides = new Set(updatedRides.map(ride => ride.driver_id));

    // Remove markers for drivers that no longer have rides to display
    this.removeObsoleteDriverMarkers(currentlyDisplayedDrivers, driversWithRides);

    updatedRides.forEach(ride => {
      this.processRideUpdate(ride);
    });
  }

  private removeObsoleteDriverMarkers(currentlyDisplayedDrivers: Set<number>, driversWithRides: Set<number>) {
    currentlyDisplayedDrivers.forEach(driverId => {
      if (!driversWithRides.has(driverId)) {
        const displayedRide = this.displayedDriverRides.get(driverId);
        if (displayedRide) {
          this.removeDriverMarker(displayedRide.rideDriverData, displayedRide.marker);
        }
      }
    });
  }

  private processRideUpdate(ride: DriverMapRideDto) {
    const shouldShow = this.allRideIds.includes(ride.ride_id);

    if (!shouldShow) {
      const existingMarker = this.rideDriverMarkers.get(ride.original_ride_id);
      if (existingMarker) {
        this.removeDriverMarker(ride, existingMarker);
      }
      return;
    }

    this.handleDriverChanges(ride);
    this.updateOrCreateMarker(ride);
  }

  private handleDriverChanges(ride: DriverMapRideDto) {
    // Check if this ride was previously displayed by a different driver
    const previousDriverId = this.findPreviousDriverForRide(ride);

    if (previousDriverId !== null) {
      const oldDisplayedRide = this.displayedDriverRides.get(previousDriverId);
      if (oldDisplayedRide) {
        this.removeDriverMarker(oldDisplayedRide.rideDriverData, oldDisplayedRide.marker);
      }
    }

    // Check if current driver already has a displayed ride (different from this one)
    const currentDisplayedRide = this.displayedDriverRides.get(ride.driver_id);

    if (currentDisplayedRide && currentDisplayedRide.rideDriverData.original_ride_id !== ride.original_ride_id) {
      // Driver is switching from one ride to another - remove the old marker first
      this.removeDriverMarker(currentDisplayedRide.rideDriverData, currentDisplayedRide.marker);
    }
  }

  private findPreviousDriverForRide(ride: DriverMapRideDto): number | null {
    for (const [driverId, displayedRide] of this.displayedDriverRides.entries()) {
      if (displayedRide.rideDriverData.original_ride_id === ride.original_ride_id && driverId !== ride.driver_id) {
        return driverId;
      }
    }
    return null;
  }

  private updateOrCreateMarker(ride: DriverMapRideDto) {
    const existingMarkerForThisRide = this.rideDriverMarkers.get(ride.original_ride_id);
    const currentDisplayedRide = this.displayedDriverRides.get(ride.driver_id);

    if (!existingMarkerForThisRide) {
      if (ride.latitude && ride.longitude) {
        this.createDriverMarker(ride);
      }
    } else if (currentDisplayedRide?.rideDriverData.original_ride_id === ride.original_ride_id) {
      if (ride.latitude && ride.longitude) {
        this.updateDriverMarker(ride, existingMarkerForThisRide);
      }
    }
  }

  clearListeners() {
    if (this.zoomChangesSubscription) {
      this.zoomChangesSubscription.unsubscribe();
    }
  }

  cleanupMapResources() {
    this.clearListeners();
    this.componentMapListeners.forEach(listener => google.maps.event.removeListener(listener));
    this.componentMapListeners = [];
    if (google?.maps?.event) {
      this.rideDriverMarkers.forEach(marker => google.maps.event.clearInstanceListeners(marker));
    }
    // Clear Marker Collections
    this.rideDriverMarkers.clear();
    this.displayedDriverRides.clear();

    // Unsubscribe ALL active Firestore listeners
    this.mapCollectionSubscription?.unsubscribe();
    this.partnersSubscription?.unsubscribe();

    this.clusterer?.clearMarkers();
    this.clusterer = null;

    // Clear Map Instance Reference held by the component
    this.mapInstance = null;

    this.infoWindow?.close();
  }

  navigateToRideDetails(rideId: number) {
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigateByUrl(`/dispatch/monitoring-center/ride-details/${rideId}`);
    } else {
      this.router.navigateByUrl(`/monitoring-center/ride-details/${rideId}`);
    }
  }

  private removeDriverMarker(driverData: DriverMapRideDto, oldMarker: google.maps.marker.AdvancedMarkerElement): void {
    oldMarker.map = null;
    this.clusterer?.removeMarker(oldMarker);

    this.rideDriverMarkers.delete(driverData.original_ride_id);
    this.displayedDriverRides.delete(driverData.driver_id);
  }

  // Update existing marker without replacing it
  private updateDriverMarker(driverData: DriverMapRideDto, existingMarker: google.maps.marker.AdvancedMarkerElement): void {
    // Update position if coordinates have changed
    const currentPos = existingMarker.position;
    const newLat = driverData.latitude ?? 0;
    const newLng = driverData.longitude ?? 0;

    let currentLat = 0;
    let currentLng = 0;

    if (currentPos) {
      // Handle both LatLng object and plain object formats
      if (typeof (currentPos as any).lat === 'function') {
        currentLat = (currentPos as google.maps.LatLng).lat();
        currentLng = (currentPos as google.maps.LatLng).lng();
      } else {
        currentLat = (currentPos as any).lat ?? 0;
        currentLng = (currentPos as any).lng ?? 0;
      }
    }

    if (!currentPos || currentLat !== newLat || currentLng !== newLng) {
      existingMarker.position = { lat: newLat, lng: newLng };
    }

    // Update marker icon/content if status has changed
    const currentDisplayedRide = this.displayedDriverRides.get(driverData.driver_id)?.rideDriverData;
    const newMarkerColor = this.determineMarkerColor(driverData);
    const currentMarkerColor = currentDisplayedRide ? this.determineMarkerColor(currentDisplayedRide) : null;

    if (newMarkerColor !== currentMarkerColor) {
      existingMarker.replaceChildren(this.createCarIcon(newMarkerColor));
    }

    // Update the displayed ride data
    this.displayedDriverRides.set(driverData.driver_id, { rideDriverData: driverData, marker: existingMarker });

    // Update tooltip content ONLY if it's currently open for THIS specific marker
    if (this.tooltipRef && this.infoWindow && this.openTooltipRideId === driverData.original_ride_id) {
      this.tooltipRef.instance.driver.set(driverData);
      // Trigger change detection if needed
      this.tooltipRef.changeDetectorRef?.detectChanges();
    }

    existingMarker.addEventListener('click', () => {
      this.currentRole == UserRole.Client ? this.navigateToRideDetails(driverData.original_ride_id) : this.openInfoWindow(driverData, existingMarker);
    });
  }

  private createDriverMarker(driverData: DriverMapRideDto) {
    const markerColor: MarkerSeverity = this.determineMarkerColor(driverData);
    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: driverData.latitude ?? 0, lng: driverData.longitude ?? 0 },
      content: this.createCarIcon(markerColor),
    });

    newMarker.addEventListener('click', () => {
      this.currentRole == UserRole.Client ? this.navigateToRideDetails(driverData.original_ride_id) : this.openInfoWindow(driverData, newMarker);
    });

    this.rideDriverMarkers.set(driverData.original_ride_id, newMarker);
    this.displayedDriverRides.set(driverData.driver_id, { rideDriverData: driverData, marker: newMarker });
    this.clusterer?.addMarker(newMarker);
  }

  private determineMarkerColor(driverData: DriverMapRideDto): MarkerSeverity {
    if (!driverData.original_ride_id) return 'success';
    if (driverData.is_offered) {
      driverData.ride_status = 'Offered';
      return 'light-warning';
    }
    if (driverData.is_accepted || driverData.in_progress) {
      if (driverData.is_accepted) driverData.ride_status = 'Accepted';
      if (driverData.in_progress) driverData.ride_status = 'In Progress';
      return 'danger';
    }
    if (driverData.is_sub_driver) return 'warning';
    return 'success';
  }

  observeZoomChanges(): Observable<[number, number]> {
    return fromEventPattern(
      handler => this.mapInstance?.addListener('zoom_changed', handler),
      (handler, listener) => google.maps.event.removeListener(listener),
    ).pipe(
      map(() => this.mapInstance?.getZoom()!),
      pairwise(),
    );
  }

  addCarMarkers(drivers: DriverMapRideDto[]) {
    if (!this.isMapAttachedHere || !this.mapInstance) return;
    this.clearMap();
    const bounds = new google.maps.LatLngBounds();

    const filteredDrivers = drivers.filter(d => d.latitude && d.longitude);
    const markers = filteredDrivers.map((d: DriverMapRideDto, i: number) => {
      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: d.latitude ?? 0, lng: d.longitude ?? 0 },
        content: this.createCarIcon(this.determineMarkerColor(d)),
      });

      bounds.extend(marker.position!);

      marker.addEventListener('click', () => {
        this.currentRole == UserRole.Client ? this.navigateToRideDetails(d.original_ride_id) : this.openInfoWindow(d, marker);
      });
      this.rideDriverMarkers.set(d.original_ride_id, marker);
      this.displayedDriverRides.set(d.driver_id, { rideDriverData: d, marker });
      return marker;
    });
    const renderer = {
      render: ({ count, position }: Cluster) => {
        // Custom cluster icon
        return new google.maps.marker.AdvancedMarkerElement({
          position,
          gmpClickable: true,
          content: this.createCarIcon(''),
        });
      },
    };

    this.clusterer = new MarkerClusterer({ markers, map: this.mapInstance, renderer });
  }

  private openInfoWindow(driver: DriverMapRideDto, marker: google.maps.marker.AdvancedMarkerElement) {
    if (this.infoWindow) {
      this.infoWindow.close();
    }

    if (this.tooltipRef) {
      this.tooltipRef.destroy();
    }
    const tooltipRef = this.createComponentContent(driver);
    this.tooltipRef = tooltipRef;
    this.openTooltipRideId = driver.original_ride_id; // Track which ride has the open tooltip

    this.infoWindow = new google.maps.InfoWindow({
      content: tooltipRef.location.nativeElement,
    });

    // Listen for info window close event to clear the tracking
    this.infoWindow.addListener('close', () => {
      this.openTooltipRideId = null;
      if (this.tooltipRef) {
        this.tooltipRef.destroy();
        this.tooltipRef = null;
      }
    });

    this.infoWindow.open(marker.map, marker);
  }

  private createComponentContent(driver: DriverMapRideDto): ComponentRef<DriverTooltipComponent> {
    const componentRef = this.viewContainerRef.createComponent(DriverTooltipComponent, {
      injector: this.injector,
    });

    componentRef.instance.driver.set(driver);
    componentRef.instance.closed.subscribe(() => this.infoWindow.close());
    return componentRef;
  }

  private createCarIcon(status: MarkerSeverity): HTMLElement {
    const div = document.createElement('div');
    div.className = 'car-marker';
    div.innerHTML = `<img class="car-img" src="/assets/google-map-assets/icons/car.svg" alt="Car">
      <div class="car-shadow ${status}"></div>`;
    return div;
  }

  clearMap() {
    if (!this.isMapAttachedHere || !this.mapInstance) return;
    this.rideDriverMarkers.forEach(m => {
      google.maps.event.clearInstanceListeners(m);
    });
    this.rideDriverMarkers.clear();
    this.displayedDriverRides.clear();
    if (this.clusterer) {
      this.clusterer.clearMarkers();
    }
  }

  ngOnDestroy() {
    this.cleanupMapResources();
    this.singleMapService.releaseMap(this.mapOwnerId);
    this.isMapAttachedHere = false;
    this.ownerActivationSub?.unsubscribe();
  }
}
